import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import usePDPPhotos from 'data-hooks/usePDPPhotos/index';
import usePDPCylindoViewer from 'data-hooks/usePDPCylindoViewer';
import useOnEsc from 'hooks/useOnEsc';
import usePDPViewGalleryImageEvent from 'data-hooks/useEvents/usePDPViewGalleryImageEvent';
import usePDPGalleryInteractedEvent from 'data-hooks/useEvents/usePDPGalleryInteractedEvent';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import RatioContainer from 'components/consumer/RatioContainer';
import Placeholder from 'components/consumer/Placeholder';
import convertImgixPropsToQueryParams from 'commons/convertImgixPropsToQueryParams';
import withImgixWidth from 'commons/withImgixWidth';
import getProductImageBaseImgixParams from 'commons/getProductImageBaseImgixParams';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import IconPlus from 'components/icons/iconPlus';
import IconMinus from 'components/icons/iconMinus';
import IconClose from 'components/icons/iconClose';
import { GALLERY_INTERACTION } from 'data-hooks/useEvents/pdpGalleryEventConstants';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import { rememberPaintedHero } from '../PDPDesktopHeroGallery/helpers/lastPaintedHero';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';
import PDPGalleryRevampCircleButton from './PDPGalleryRevampCircleButton';
import PDPGalleryRevampControlsBar from './PDPGalleryRevampControlsBar';
import PDPGalleryRevampLifestyleRail from './PDPGalleryRevampLifestyleRail';
import PDPGalleryRevampExpandedGallery from './PDPGalleryRevampExpandedGallery';
import PDPGalleryRevampSpinZoomHint from './PDPGalleryRevampSpinZoomHint';
import clickViewerControl, { closeViewerAR } from './clickViewerControl';
import useCylindoARActive from '../hooks/useCylindoARActive';
import useCylindoBootOverlay from '../hooks/useCylindoBootOverlay';

// Goes into expandedGalleryIndex in place of a photo index when the gallery opens on the video tile
const EXPANDED_VIDEO_TILE = 'video';

// Cap the gallery like the legacy maxWidth cap so image + controls stay in the viewport
const GALLERY_RATIO = 1 / 2;
const EMPTY_ARRAY = [];
const VERTICAL_SPACE_AROUND = 32 + 45 + 90;
const RAIL_TOTAL_WIDTH = 72; // 56px rail + 16px gap
const GALLERY_MAX_WIDTH = `calc((100vh - ${VERTICAL_SPACE_AROUND}px) / ${GALLERY_RATIO} + ${RAIL_TOTAL_WIDTH}px)`;
const REVAMP_VIEWER_ID = 'PDPDesktopGalleryRevampViewer';

// Caps the plain-<img> hero so imgix never serves an oversized source (CON-8281)
const SSR_MAIN_IMAGE_MAX_WIDTH = 1600;

// The dimension shot fills the same box as the hero, so it gets the same cap
const DIMENSION_SHOT_IMAGE_WIDTH = 1600;

// Redesigned desktop gallery (CON-8444), behind ?galleryRevamp=1 for now
const PDPDesktopGalleryRevamp = ({
  slug,
  productHeroImage = null,
  productCylindoHeroImage = null
}) => {
  const {
    loading,
    error,
    data
  } = usePDPPhotos(null, slug);
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const {
    resetGalleryImageTracking,
    viewGalleryImageEvent
  } = usePDPViewGalleryImageEvent();
  const {
    trackGalleryInteracted
  } = usePDPGalleryInteractedEvent();
  const {
    cylindoFeatures,
    isCylindoAvailable,
    cylindoConfigurationPhotos,
    isCylindoConfigurationPhotos,
    isMechanicalOpen,
    toggleMechanicalOpen,
    dimensionShotUrls,
    selectedDimensionShotIndex,
    shouldShowDimensionThumbnail,
    setSelectedDimensionShotIndex
  } = usePDPCylindoViewer({
    isPDPDesktop: true,
    deferCylindoFetch: true
  });
  const [isMeasureActive, setIsMeasureActive] = useState(false);
  const [expandedGalleryIndex, setExpandedGalleryIndex] = useState(null);

  // Each opening is its own run of images, so the dedupe starts clean
  useEffect(() => {
    if (expandedGalleryIndex != null) {
      resetGalleryImageTracking();
    }
  }, [expandedGalleryIndex, resetGalleryImageTracking]);
  const {
    isARActive,
    rearm: rearmARWatcher
  } = useCylindoARActive(REVAMP_VIEWER_ID);
  const {
    configurationPhotos: nonCylindoConfigurationPhotos,
    dimensionalImage,
    enableCrops,
    introVideoIds,
    isConfigFlipped,
    productName,
    selectedPhoto,
    studioPhotos: allStudioPhotos
  } = data ?? {};
  const configurationPhotos = useRemoveBrokenImageUrls(isCylindoConfigurationPhotos ? cylindoConfigurationPhotos : nonCylindoConfigurationPhotos);
  const studioPhotos = useRemoveBrokenImageUrls(allStudioPhotos);
  const closeMeasureView = useCallback(() => setIsMeasureActive(false), []);
  useOnEsc(isMeasureActive && expandedGalleryIndex == null, closeMeasureView);
  const showDecideLaterOverlay = pdpSelectedCustomizerTab === 'decide-later';

  // Falls back to the static dimensional image when there are no Cylindo shots
  const dimensionShotsToShow = useMemo(() => {
    if (dimensionShotUrls.length) {
      return dimensionShotUrls;
    }
    if (dimensionalImage?.src) {
      return [{
        src: withImgixWidth(dimensionalImage.src, DIMENSION_SHOT_IMAGE_WIDTH),
        dimensionCode: 'static',
        id: 'static-dimensional'
      }];
    }
    return EMPTY_ARRAY;
  }, [dimensionShotUrls, dimensionalImage]);
  const introVideoId = introVideoIds?.desktop;
  const showMeasureView = isMeasureActive && !!dimensionShotsToShow.length;
  const showCylindoViewer = isCylindoAvailable && isCylindoConfigurationPhotos && !showMeasureView;

  // Server-resolved hero for the pre-photos paint, same as the legacy gallery
  // (CON-8281 LCP). Cylindo frames must stay raw - never through imgix (CON-8293).
  const ssrFallbackPhoto = productCylindoHeroImage?.src ? {
    type: 'cylindo',
    src: productCylindoHeroImage.src
  } : productHeroImage?.src ? {
    type: productHeroImage.src.includes('/configurations/') ? 'configuration' : 'studio',
    src: productHeroImage.src
  } : null;
  const fallbackPhoto = selectedPhoto?.src && selectedPhoto.type !== 'dimensional' ? selectedPhoto : configurationPhotos?.[0] ?? studioPhotos?.[0] ?? ssrFallbackPhoto;

  // The viewer boots behind the photo it replaces. The photo covers it, then
  // fades out once the viewer paints, so the hero never vanishes or jumps size
  // while the viewer starts up (CON-8444).
  const {
    overlayPhoto,
    isViewerReady,
    onViewerLoaded
  } = useCylindoBootOverlay({
    isViewerActive: showCylindoViewer,
    photo: fallbackPhoto
  });
  const heroPhoto = overlayPhoto ?? fallbackPhoto;

  // Plain <img> with the crop format pinned to desktop (this gallery only shows
  // at lg+): JBProductImage follows useScreen's mobile-first SSR default, which
  // re-frames and re-fetches the hero after hydration (CON-8543)
  const heroImgSrc = useMemo(() => {
    if (!heroPhoto?.src) {
      return null;
    }
    if (heroPhoto.type === 'cylindo') {
      return heroPhoto.src;
    }
    return convertImgixPropsToQueryParams({
      width: SSR_MAIN_IMAGE_MAX_WIDTH,
      ...getProductImageBaseImgixParams({
        type: heroPhoto.type,
        src: heroPhoto.src,
        crops: heroPhoto.cropData,
        enableCrops,
        isMobileOrTablet: false
      }),
      ...getMainImageImgixPropsForPhoto(heroPhoto, enableCrops)
    }, heroPhoto.src, false);
  }, [heroPhoto, enableCrops]);
  if (loading) {
    return <div className="mb-8 ml-auto mr-auto relative" style={{
      maxWidth: GALLERY_MAX_WIDTH
    }} data-xc="PDPDesktopGalleryRevamp">
        <RatioContainer ratio={GALLERY_RATIO}>
          {heroImgSrc ? <img className="h-full w-full object-contain object-center select-none" src={heroImgSrc} alt={productHeroImage?.alt || ''} width={1600} height={2400} loading="eager" fetchPriority="high" decoding="async" /> : <Placeholder type="rect" loading />}
        </RatioContainer>
      </div>;
  }
  if (!data) {
    return null;
  }
  if (error) {
    return 'Something went wrong, please try again';
  }
  const isExpandedGalleryOpen = expandedGalleryIndex != null;
  const selectedConfigIndex = Math.max(0, configurationPhotos.findIndex(({
    src
  }) => src === selectedPhoto?.src));
  const isConfigPhotoSelected = selectedPhoto?.type === 'configuration' || selectedPhoto?.type === 'cylindo';
  return <div className="mb-8 ml-auto mr-auto relative" style={{
    maxWidth: GALLERY_MAX_WIDTH
  }} data-xc="PDPDesktopGalleryRevamp">
      <div className="flex gap-4">
        <PDPGalleryRevampLifestyleRail introVideoId={introVideoId} onExpand={setExpandedGalleryIndex}
      // Opens the gallery on the video rather than swapping the hero for it
      onSelectVideo={() => setExpandedGalleryIndex(EXPANDED_VIDEO_TILE)} photos={studioPhotos} trackGalleryInteracted={trackGalleryInteracted} />

        <div className="flex-1 min-w-0">
          <RatioContainer className={classnames('bg-transparent border-0 focus:outline-0',
        // Cylindo's AR panel ships a slate-blue backdrop; restyle it to Joybird
        // teal and keep its close button visible while our fabs stay hidden
        '[&_cylindo-viewer::part(fab)]:hidden [&_cylindo-viewer::part(qr-fab)]:!flex [&_cylindo-viewer::part(qr-backdrop)]:!bg-[rgba(16,124,140,0.8)] [&_cylindo-viewer::part(qr-dialog)]:border-2 [&_cylindo-viewer::part(qr-dialog)]:border-solid [&_cylindo-viewer::part(qr-dialog)]:border-brand')} ratio={GALLERY_RATIO}>
            {showMeasureView && <PDPDimensionShotViewer dimensionShots={dimensionShotsToShow} selectedDimensionShotIndex={selectedDimensionShotIndex} onIndexChange={setSelectedDimensionShotIndex} arrowButtonClass="w-9 h-9 bg-white border border-solid border-gray-light6 hover:bg-gray-light9 [&_svg_path]:fill-gray-dark" className="h-full w-full" lazy={false} onLoad={markLuxPageNavigationEnd} />}

            {!showMeasureView && (!showCylindoViewer || !!overlayPhoto) && !!heroImgSrc && <img className={classnames('h-full w-full object-contain object-center select-none', {
            'scale-x-[-1]': heroPhoto.type !== 'studio' && isConfigFlipped,
            // Covers the booting viewer, fades out once it paints
            'absolute inset-0 z-[1] pointer-events-none transition-opacity duration-500': !!overlayPhoto,
            'opacity-0': !!overlayPhoto && isViewerReady
          })} src={heroImgSrc} alt={productName ? String(productName) : ''} width={1600} height={2400} loading="eager" fetchPriority="high" decoding="async" onLoad={event => {
            // Feeds the panel boundary's hold fallback (CON-8543)
            rememberPaintedHero(event?.target?.currentSrc);
            markLuxPageNavigationEnd();
          }} />}

            {isCylindoAvailable && <div className={classnames('w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden',
          // Product asked for 15-20% off the desktop render, which read as
          // oversized for the gallery box; 0.85 is the low end (CON-8444).
          // Desktop only - mobile is full-bleed and doesn't need it.
          '[&_cylindo-viewer]:scale-[0.85] [&_cylindo-viewer]:origin-center', {
            invisible: !showCylindoViewer
          })}>
                <PDPCylindo3dViewer containerId={REVAMP_VIEWER_ID} cylindoFeatures={cylindoFeatures} rotateIndex={isConfigPhotoSelected ? selectedConfigIndex : -1} onViewerLoaded={onViewerLoaded} />
              </div>}

            {showCylindoViewer && !isARActive && <PDPGalleryRevampSpinZoomHint />}

            {showDecideLaterOverlay && <PDPDecideLaterOverlay />}
          </RatioContainer>
        </div>
      </div>

      <PDPGalleryRevampControlsBar isMeasureActive={showMeasureView}
    // Only one of measure / open can be active, so turning measure on
    // closes the mechanical view the same way opening clears measure
    onToggleMeasure={() => {
      if (!showMeasureView && isMechanicalOpen) {
        toggleMechanicalOpen();
      }
      // Only the off -> on transition counts as "activated" (CON-8512)
      if (!showMeasureView) {
        trackGalleryInteracted(GALLERY_INTERACTION.MEASURE_ACTIVATED);
      }
      setIsMeasureActive(!showMeasureView);
    }} showMeasure={shouldShowDimensionThumbnail} isMechanicalOpen={isMechanicalOpen} toggleMechanicalOpen={() => {
      // Last interaction wins: toggling open/close leaves measure view
      setIsMeasureActive(false);
      // Only the closed -> open transition counts as "activated" (CON-8512)
      if (!isMechanicalOpen) {
        trackGalleryInteracted(GALLERY_INTERACTION.OPEN_STATE_ACTIVATED);
      }
      toggleMechanicalOpen();
    }} isARActive={isARActive} onLaunchViewerAR={isCylindoAvailable && isCylindoConfigurationPhotos ? () => {
      setIsMeasureActive(false);
      clickViewerControl(REVAMP_VIEWER_ID, 'view-in-ar');
      rearmARWatcher();
    } : null} onCloseViewerAR={() => closeViewerAR(REVAMP_VIEWER_ID)} />

      {/* Overlays the hero + controls area; viewer stays mounted underneath */}
      {isExpandedGalleryOpen && <div className="absolute inset-0 z-[3] bg-gray-light8">
          <PDPGalleryRevampExpandedGallery initialIndex={expandedGalleryIndex === EXPANDED_VIDEO_TILE ? 0 : expandedGalleryIndex} initialVideoSelected={expandedGalleryIndex === EXPANDED_VIDEO_TILE} introVideoId={introVideoId} onClose={() => setExpandedGalleryIndex(null)} photos={studioPhotos} productName={productName} trackGalleryInteracted={trackGalleryInteracted} viewGalleryImageEvent={viewGalleryImageEvent} />
        </div>}

      {/* Hidden during AR so it can't cover Cylindo's close button (CON-8444) */}
      <div className={classnames('flex justify-between left-20 absolute right-4 top-4 z-[2]', {
      hidden: isARActive
    })}>
        <PDPGalleryLabelsAndViewingCount mode="pdp" />
        <div className="flex flex-col gap-2 items-end">
          <PDPFavoriteButton />
          {showMeasureView && <PDPGalleryRevampCircleButton ariaLabel="Close measurements" onClick={() => setIsMeasureActive(false)}>
              <IconClose width={9} height={9} />
            </PDPGalleryRevampCircleButton>}
          {showCylindoViewer && <>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom in" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-in')}>
                <IconPlus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom out" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-out')}>
                <IconMinus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
            </>}
        </div>
      </div>
    </div>;
};
PDPDesktopGalleryRevamp.propTypes = {
  slug: PropTypes.string,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPDesktopGalleryRevamp;